/**
 * /earthworks/change-orders/:id — one change order (EW-5).
 *
 *   • reason, description and lines change while DRAFT or SENT. Editing a sent
 *     one returns it to draft and closes its link (the API does that, and says
 *     so here before the edit), so a customer never approves content that moved;
 *   • Send mints (or rotates) the customer link and, with an email, mails it.
 *     The link url is shown either way so the office can copy it;
 *   • Void — draft, sent or declined; an approved change order is part of the
 *     job's price and cannot be voided.
 *
 * Approval is the customer's: their signature on the crew's phone (the field
 * screen) or the emailed link. There is no office approve.
 *
 * EW-6 (Jason 2026-11-01): an APPROVED change order is already customer-
 * signed, so the office (admin / office staff) may invoice it directly —
 * "Invoice this change order" bills its lines on neither a live ticket nor a
 * live invoice (POST /earthworks/invoices/from-change-order) and opens the
 * invoice. A job already drawn from its quote answers 409 billing_overlap;
 * the warning is shown with "Confirm and invoice anyway".
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Activity, Copy, FileOutput, FilePen, Send, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useEwMeta } from '../../hooks/useEarthworks';
import {
  useAddEwCoLine,
  useEwChangeOrder,
  useRemoveEwCoLine,
  useSendEwChangeOrder,
  useUpdateEwChangeOrder,
  useUpdateEwCoLine,
  useVoidEwChangeOrder,
} from '../../hooks/useEwTickets';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { ewInstant, ewRefusal } from '../../lib/earthworks';
import { canWriteEwInvoices, ewBillingOverlapWarning, ewCoCanInvoice, ewInvoiceStatusSkin } from '../../lib/ew-invoices';
import { useCreateEwInvoiceFromChangeOrder } from '../../hooks/useEwInvoices';
import { EwOverlapConfirm } from './invoices/EwOverlapConfirm';
import { canWorkEwTickets, ewCoEditable, ewCoSendable, ewCoSkin, ewCoVoidable, ewSignedVia } from '../../lib/ew-tickets';
import type { EwCoSent } from '../../types/earthworks';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';
import { ewCoMoney } from './EwChangeOrdersScreen';
import { EwLinesPanel } from './EwLinesPanel';
import {
  isNotFound,
  EW_EYEBROW,
  EW_INPUT,
  EW_PRIMARY,
  EW_SECONDARY,
  EwBadge,
  EwDialog,
  EwEmpty,
  EwField,
  EwFormError,
  EwLoading,
  EwPanel,
  EwSpec,
} from './EwBits';

export default function EwChangeOrderDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList('/earthworks/change-orders');
  const { user } = useAuth();
  const canWork = canWorkEwTickets(user?.role);
  const tz = useTenantTimeZone();
  const query = useEwChangeOrder(id);
  const metaQ = useEwMeta();
  const catalog = metaQ.data?.data ?? null;
  const meta = metaQ.data?.meta ?? null;
  const addLine = useAddEwCoLine();
  const updateLine = useUpdateEwCoLine();
  const removeLine = useRemoveEwCoLine();
  const update = useUpdateEwChangeOrder();
  const send = useSendEwChangeOrder();
  const voidCo = useVoidEwChangeOrder();
  const invoiceCo = useCreateEwInvoiceFromChangeOrder();
  const navigate = useNavigate();
  const canInvoice = canWriteEwInvoices(user?.role);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [overlap, setOverlap] = useState<string | null>(null);

  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<EwCoSent | null>(null);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  const co = query.data ?? null;
  useEffect(() => {
    setReason(co?.reason ?? '');
    setDescription(co?.description ?? '');
  }, [co?.id, co?.reason, co?.description]);
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const editable = canWork && !!co && ewCoEditable(co.status);
  const linkUrl = sent?.data?.link?.url ?? co?.link?.url ?? null;

  const doSend = () => {
    if (!co) return;
    setDialogError(null);
    send.mutate({ id: co.id, email: email.trim() || undefined }, {
      onSuccess: (r) => {
        setSent(r);
        if (r.emailed && !r.emailed.sent) toast.error(`The link is ready but the email to ${r.emailed.to} did not go: ${r.emailed.error ?? 'unknown error'}`);
        else toast.success(r.emailed ? `Sent to ${r.emailed.to}` : 'Link ready to copy');
      },
      onError: (err) => setDialogError(ewRefusal(err, 'The change order could not be sent.')),
    });
  };
  const doVoid = () => {
    if (!co) return;
    setDialogError(null);
    if (!voidReason.trim()) return setDialogError('Say why the change order is void.');
    voidCo.mutate({ id: co.id, reason: voidReason.trim() }, {
      onSuccess: () => { toast.success(`${co.coNumber} voided`); setVoiding(false); setVoidReason(''); },
      onError: (err) => setDialogError(ewRefusal(err, 'The change order could not be voided.')),
    });
  };
  const saveText = () => {
    if (!co) return;
    setEditError(null);
    if (!reason.trim()) return setEditError('A change order needs a reason.');
    update.mutate({ id: co.id, body: { reason: reason.trim(), description: description.trim() || null } }, {
      onSuccess: () => toast.success('Change order saved'),
      onError: (err) => setEditError(ewRefusal(err, 'The change order could not be saved.')),
    });
  };
  const doInvoice = (confirm = false) => {
    if (!co) return;
    setInvoiceError(null);
    setOverlap(null);
    invoiceCo.mutate({ change_order_id: co.id, ...(confirm ? { confirm_overlap: true } : {}) }, {
      onSuccess: (inv) => {
        toast.success(inv?.invoiceNumber ? `${inv.invoiceNumber} drafted` : 'Invoice drafted');
        if (inv?.id) navigate(`/earthworks/invoices/${inv.id}`);
      },
      onError: (err) => {
        const warning = ewBillingOverlapWarning(err);
        if (warning) setOverlap(warning);
        else setInvoiceError(ewRefusal(err, 'The change order could not be invoiced.'));
      },
    });
  };
  const copyLink = async () => {
    if (!linkUrl) return;
    try {
      await navigator.clipboard.writeText(linkUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Copy did not work here. Select the link and copy it.');
    }
  };

  return (
    <div data-testid="ew-co-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={`${EW_EYEBROW} · Change order`}
        title={co?.coNumber || (query.isLoading ? 'Change order' : '—')}
        description={co ? `${co.jobNumber} · ${co.customerName ?? '—'} · ${co.siteName ?? '—'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          co && canWork ? (
            <div className="flex flex-wrap gap-2" data-testid="ew-co-actions">
              {ewCoSendable(co.status) && (
                <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => { setDialogError(null); setSent(null); setSending(true); }} data-testid="ew-co-send">
                  <Send className="size-4" aria-hidden="true" />
                  {co.status === 'sent' ? 'Send again' : 'Send to customer'}
                </button>
              )}
              {canInvoice && ewCoCanInvoice(co) && (
                <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => doInvoice()} disabled={invoiceCo.isPending} data-testid="ew-co-invoice">
                  <FileOutput className="size-4" aria-hidden="true" />
                  {invoiceCo.isPending ? 'Invoicing…' : 'Invoice this change order'}
                </button>
              )}
              {ewCoVoidable(co.status) && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDialogError(null); setVoiding(true); }} data-testid="ew-co-void">
                  <XCircle className="size-4" aria-hidden="true" />
                  Void
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Activity className="size-4" aria-hidden="true" />, label: 'Status', tone: 'neutral', loading: query.isLoading, value: co ? ewCoSkin(co.status).label : '—' },
          { icon: <FilePen className="size-4" aria-hidden="true" />, label: 'Amount', tone: 'neutral', loading: query.isLoading, value: co ? ewCoMoney(co) : '—' },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canWork ? (
            <EwPanel eyebrow="Change order" title="Change order">
              <EwEmpty>Change orders carry prices, so they are for the office, supervisors and dispatch.</EwEmpty>
            </EwPanel>
          ) : query.isLoading ? (
            <EwLoading label="Loading change order" />
          ) : notFound || (!failure && !co) ? (
            <EwPanel eyebrow="Change order" title="Change order">
              <EwEmpty>This change order was not found. It may have been deleted.</EwEmpty>
            </EwPanel>
          ) : failure || !co ? (
            <EwPanel eyebrow="Change order" title="Change order">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this change order" />
            </EwPanel>
          ) : (
            <>
              {overlap && (
                <EwOverlapConfirm message={overlap} pending={invoiceCo.isPending} onConfirm={() => doInvoice(true)} onCancel={() => setOverlap(null)} />
              )}
              {invoiceError && (
                <p role="alert" className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text" data-testid="ew-co-invoice-error">{invoiceError}</p>
              )}
              {co.status === 'sent' && (
                <p className="rounded-fi-control border border-fi-amber/40 bg-fi-amber/10 px-4 py-3 text-sm text-fieldiq-text" data-testid="ew-co-sent-note">
                  Sent{co.link?.sentTo ? ` to ${co.link.sentTo}` : ''} · {ewInstant(co.sentAt, tz)}. Changing anything returns it to draft and closes the customer's link; send it again afterwards.
                </p>
              )}
              <EwPanel eyebrow="Change order" title="Details" testId="ew-co-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <EwSpec label="Status" value={<EwBadge skin={ewCoSkin(co.status)} value={co.status} />} />
                  <EwSpec label="Job" value={<Link className="font-bold text-fi-amber underline" to={`/earthworks/jobs/${co.jobId}`}>{co.jobNumber}</Link>} />
                  <EwSpec label="Customer" value={co.customerName || '—'} />
                  <EwSpec label="Site" value={co.siteName || '—'} />
                  <EwSpec label="Approved" value={co.approvedAt ? `${ewSignedVia(co.approvedVia)} · ${ewInstant(co.approvedAt, tz)}` : '—'} />
                  <EwSpec label="Signer" value={co.signerName ? `${co.signerName}${co.signerTitle ? `, ${co.signerTitle}` : ''}` : '—'} />
                  <EwSpec label="Billed lines" value={`${co.billedLineCount} of ${co.lineCount}`} />
                  {(co.directInvoices?.length ?? 0) > 0 && (
                    <EwSpec
                      label="Invoiced directly"
                      value={
                        <span data-testid="ew-co-direct-invoices">
                          {(co.directInvoices ?? []).map((i, n) => (
                            <span key={i.id}>
                              {n > 0 ? ', ' : ''}
                              <Link className="font-mono underline" to={`/earthworks/invoices/${i.id}`}>{i.invoiceNumber}</Link>
                              {` · ${ewInvoiceStatusSkin(i.status).label}`}
                            </span>
                          ))}
                        </span>
                      }
                    />
                  )}
                  <EwSpec label="Currency" value={co.currency || '—'} />
                </dl>
                {co.declineReason && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">Declined: {co.declineReason}</p>}
                {co.voidReason && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">Void{co.voidedByName ? ` (${co.voidedByName})` : ''}: {co.voidReason}</p>}
                {co.signatureUrl && <div className="border-t border-fieldiq-border px-4 py-3"><img src={co.signatureUrl} alt="Customer signature" className="h-20 rounded bg-white p-1" /></div>}
              </EwPanel>

              <EwPanel eyebrow="Change order" title="Reason" testId="ew-co-text">
                {editable ? (
                  <div className="space-y-3 px-4 py-4">
                    <EwFormError message={editError} />
                    <EwField label="Reason">
                      <input className={EW_INPUT} aria-label="Change order reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </EwField>
                    <EwField label="Description (optional)">
                      <textarea className={`${EW_INPUT} h-20 py-2`} aria-label="Change order description" value={description} onChange={(e) => setDescription(e.target.value)} />
                    </EwField>
                    <div className="flex justify-end">
                      <button type="button" className={EW_SECONDARY} onClick={saveText} disabled={update.isPending || (reason === co.reason && description === (co.description ?? ''))}>
                        {update.isPending ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-3 text-sm text-fieldiq-text">
                    <p className="font-bold text-white">{co.reason}</p>
                    {co.description && <p className="mt-1 whitespace-pre-wrap">{co.description}</p>}
                  </div>
                )}
              </EwPanel>

              <EwLinesPanel
                eyebrow="Change order"
                title="Lines"
                testId="ew-co-lines"
                lines={co.lines.map((l) => ({
                  id: l.id, kind: l.kind, kindLabel: l.kindLabel, description: l.description, unit: l.unit, unitLabel: l.unitLabel,
                  quantity: l.quantity, rate: l.rate, amount: l.amount, note: l.billed ? 'On a ticket' : null,
                }))}
                currency={co.currency}
                editable={editable}
                catalog={catalog}
                meta={meta}
                total={co.status === 'approved' ? co.subtotal : co.linesTotal}
                totalLabel={co.status === 'approved' ? 'Approved subtotal' : 'Lines total'}
                pending={addLine.isPending || updateLine.isPending || removeLine.isPending}
                onAdd={(body) => addLine.mutateAsync({ id: co.id, body })}
                onUpdate={(lineId, body) => updateLine.mutateAsync({ id: co.id, lineId, body })}
                onRemove={(lineId) => removeLine.mutateAsync({ id: co.id, lineId })}
              />

              {co.link && (
                <EwPanel eyebrow="Customer" title="Approval link" testId="ew-co-link">
                  <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                    <EwSpec label="Sent to" value={co.link.sentTo || 'Not emailed'} />
                    <EwSpec label="Sent" value={ewInstant(co.link.sentAt, tz)} />
                    <EwSpec label="First viewed" value={ewInstant(co.link.firstViewedAt, tz)} />
                    <EwSpec label="Expires" value={ewInstant(co.link.expiresAt, tz)} />
                  </dl>
                  {linkUrl && co.status === 'sent' && (
                    <div className="flex flex-wrap items-center gap-2 border-t border-fieldiq-border px-4 py-3">
                      <code className="min-w-0 break-all text-xs text-fieldiq-text" data-testid="ew-co-link-url">{linkUrl}</code>
                      <button type="button" className={EW_SECONDARY} onClick={copyLink}><Copy className="size-4" aria-hidden="true" />Copy</button>
                    </div>
                  )}
                </EwPanel>
              )}
            </>
          )}
        </div>
      </section>

      {co && (
        <>
          <EwDialog
            open={sending}
            title={`Send ${co.coNumber}`}
            onClose={() => setSending(false)}
            footer={
              sent ? (
                <button type="button" className={EW_PRIMARY} onClick={() => setSending(false)}>Done</button>
              ) : (
                <>
                  <button type="button" className={EW_SECONDARY} onClick={() => setSending(false)} disabled={send.isPending}>Not now</button>
                  <button type="button" className={EW_PRIMARY} onClick={doSend} disabled={send.isPending}>{send.isPending ? 'Sending…' : email.trim() ? 'Email the link' : 'Make the link'}</button>
                </>
              )
            }
          >
            <EwFormError message={dialogError} />
            {sent ? (
              <div className="space-y-2 text-sm text-fieldiq-text" data-testid="ew-co-sent">
                {sent.emailed ? (
                  sent.emailed.sent ? <p>Emailed to {sent.emailed.to}.</p> : <p>The email to {sent.emailed.to} did not go ({sent.emailed.error ?? 'unknown error'}). Copy the link instead.</p>
                ) : (
                  <p>The link is ready. Copy it to the customer.</p>
                )}
                {sent.data?.link?.url ? <code className="block break-all rounded bg-fieldiq-dark p-2 text-xs" data-testid="ew-co-sent-url">{sent.data.link.url}</code> : <p>—</p>}
                <p className="text-xs text-fieldiq-muted">The link expires on {ewInstant(sent.data?.link?.expiresAt ?? null, tz)}. Sending again makes a new link and the old one stops working.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-fieldiq-text">Makes a link the customer opens to approve or decline {co.coNumber} for {ewCoMoney(co)}. Leave the email blank to copy the link yourself.</p>
                <div className="mt-3">
                  <EwField label="Customer email (optional)">
                    <input className={EW_INPUT} type="email" aria-label="Customer email" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </EwField>
                </div>
              </>
            )}
          </EwDialog>
          <EwDialog
            open={voiding}
            title={`Void ${co.coNumber}`}
            onClose={() => setVoiding(false)}
            footer={
              <>
                <button type="button" className={EW_SECONDARY} onClick={() => setVoiding(false)} disabled={voidCo.isPending}>Keep it</button>
                <button type="button" className={EW_PRIMARY} onClick={doVoid} disabled={voidCo.isPending}>{voidCo.isPending ? 'Voiding…' : 'Void change order'}</button>
              </>
            }
          >
            <EwFormError message={dialogError} />
            <p className="text-sm text-fieldiq-text">A void change order is never billed, and its customer link stops working.</p>
            <div className="mt-3">
              <EwField label="Reason">
                <textarea className={`${EW_INPUT} h-20 py-2`} aria-label="Void reason" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
              </EwField>
            </div>
          </EwDialog>
        </>
      )}
    </div>
  );
}
